import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PlacementResources.css';
import { fetchDsaLeaderboard } from '../api/resources';
import { fetchAllDrivesDetailed, fetchPlacementStats, fetchFilterOptions } from '../api/placement';
import {
  addResumeFromPdf,
  askPlacementQuestion,
  deleteResume,
  listResumes
} from '../api/mcp';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import placementHero3d from '../assets/placement-career-visual.png';

const driveFolder = 'https://drive.google.com/drive/folders/1UlIxN-hqY6FrOlk_9kOJfj3-TvWJL4ru';

const CP_PROFILE_ORDER = ['leetcode', 'codeforces', 'codechef'];

const PROVIDER_PROFILE_ORDER = {
  codeforces: ['codeforces', 'leetcode', 'codechef'],
  leetcode: ['leetcode', 'codeforces', 'codechef']
};

const LEADERBOARD_PROVIDER_META = {
  codeforces: {
    title: 'Codeforces rating leaders',
    description: 'Live snapshot of IIIT Network members climbing the Codeforces ladder.'
  },
  leetcode: {
    title: 'LeetCode contest rating leaders',
    description: 'Top contest performers sharing their LeetCode grind with the community.'
  }
};

const createEmptyProviderData = () => ({ entries: [], totalProfiles: 0 });

const createEmptyLeaderboardState = () => ({
  codeforces: createEmptyProviderData(),
  leetcode: createEmptyProviderData()
});

const toSafeCount = (value, fallback = 0) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const highlightStats = [
  {
    value: '12+',
    label: 'curated templates',
    blurb: 'ATS-friendly resumes, cover letters, and portfolio samples collected from recent hires.'
  },
  {
    value: '30+',
    label: 'interview drills',
    blurb: 'Topic-wise question sets, mock interview scripts, and whiteboard prompts.'
  },
  {
    value: '8 weeks',
    label: 'structured roadmap',
    blurb: 'Follow a two-month readiness plan tailored for university placements.'
  }
];

const resourceCollections = [
  {
    id: 'resume',
    icon: '🧾',
    title: 'Resume & Portfolio Prep',
    description: 'Polish your profile with proven templates, pitch decks, and storytelling guides.',
    ctaLabel: 'Browse resume toolkit',
    resources: [
      {
        title: 'ATS-ready resume template',
        type: 'Google Docs',
        summary: 'Clean, recruiter-approved template with inline guidance on quantifying impact.',
        link: `${driveFolder}#resume-template`,
        tags: ['resume', 'ats', 'template']
      },
      {
        title: 'Portfolio & GitHub audit checklist',
        type: 'PDF',
        summary: 'Checklist to keep your public work sharp—README structure, project positioning, and accessibility.',
        link: `${driveFolder}#portfolio-checklist`,
        tags: ['portfolio', 'github']
      },
      {
        title: 'Cover letter snippet library',
        type: 'Docs',
        summary: 'Modular paragraphs for quick customization across companies and roles.',
        link: `${driveFolder}#cover-letter-library`,
        tags: ['cover letter', 'communication']
      }
    ]
  },
  {
    id: 'dsa',
    icon: '⚡',
    title: 'DSA & Competitive Programming',
    description: 'Track your progress with live leaderboards and access curated problem sheets.',
    ctaLabel: 'Browse DSA resources',
    resources: [
      {
        title: 'Standard DSA Checklist',
        type: 'PDF',
        summary: 'A comprehensive list of patterns and problems to solve.',
        link: `${driveFolder}#dsa-checklist`,
        tags: ['dsa', 'checklist']
      }
    ]
  }
];

const DEFAULT_RESUME_FORM = {
  email: '',
  name: ''
};

const DEFAULT_ASSISTANT_TOPK = 3;

const RESUME_ASSISTANT_PROMPTS = [
  'Summarize strengths of the frontend candidates in the pool.',
  'Who is the best fit for a data analyst internship and why?',
  'List candidates with Kubernetes or cloud experience.',
  'Compare the top two resumes for backend roles.'
];

// ============== PLACEMENT DASHBOARD DATA ==============
// Now fetched from FastAPI pipeline


// Parse CTC from string like "₹12 LPA" or "₹40K/month" to number (LPA)
const parseCTC = (ctcStr) => {
  if (!ctcStr) return 0;
  // Normalize: remove commas, lowercase
  const normalized = ctcStr.toString().toLowerCase().replace(/,/g, '');
  // Extract numeric value
  const match = normalized.match(/(\d+(?:\.\d+)?)/);
  if (!match) return 0;
  let num = parseFloat(match[1]);
  // Handle Units
  if (normalized.includes('month') || normalized.includes('pm')) {
    if (num < 100 && normalized.includes('k')) {
      num *= 1000;
    }
    return (num * 12) / 100000;
  }
  // Handle LPA / Lakhs explicitly
  if (normalized.includes('lpa') || normalized.includes('lakh')) {
    return num;
  }
  // Handle Raw Numbers
  if (num >= 200000) return num / 100000;
  if (num > 1000) return (num * 12) / 100000;
  if (num > 100) return num / 100;
  return num;
};

// Get date from drive
const getDate = (drive) => {
  if (drive.drive_date) return new Date(drive.drive_date);
  if (drive.posted_at) return new Date(drive.posted_at);
  if (drive.registration_deadline) return new Date(drive.registration_deadline);
  if (drive.created_at) return new Date(drive.created_at);
  return new Date();
};

const PlacementResources = () => {
  const { user } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState(() => createEmptyLeaderboardState());
  const [leaderboardSummary, setLeaderboardSummary] = useState(null);
  const [leaderboardStatus, setLeaderboardStatus] = useState('idle');
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [showLabels, setShowLabels] = useState(false);
  const [resumeForm, setResumeForm] = useState(DEFAULT_RESUME_FORM);
  const [resumes, setResumes] = useState([]);
  const [resumeStatus, setResumeStatus] = useState('idle');
  const [resumeError, setResumeError] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileError, setResumeFileError] = useState(null);
  const [assistantQuestion, setAssistantQuestion] = useState('');
  const [assistantTopK, setAssistantTopK] = useState(DEFAULT_ASSISTANT_TOPK);
  const [assistantMessages, setAssistantMessages] = useState(() => ([
    {
      id: 'assistant-welcome',
      role: 'assistant',
      content: 'Upload resumes to the shared pool, then ask anything. I will answer using the resumes I have access to.'
    }
  ]));
  const [assistantStatus, setAssistantStatus] = useState('idle');
  const [assistantError, setAssistantError] = useState(null);
  const assistantMessagesRef = useRef(null);
  const resolvedEmail = user?.email || resumeForm.email;

  // Placement Dashboard State
  const [drives, setDrives] = useState([]);
  const [drivesStatus, setDrivesStatus] = useState('idle');
  const [drivesError, setDrivesError] = useState(null);
  const [placementStats, setPlacementStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ companies: [], batches: [], statuses: [], drive_types: [] });

  // Filter state
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedDriveType, setSelectedDriveType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch placement drives and stats on mount
  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const loadPlacementData = async () => {
      setDrivesStatus('loading');
      setDrivesError(null);
      try {
        const [drivesData, statsData, filtersData] = await Promise.all([
          fetchAllDrivesDetailed({ signal: controller.signal }),
          fetchPlacementStats({ signal: controller.signal }),
          fetchFilterOptions({ signal: controller.signal })
        ]);
        if (!isActive) return;
        setDrives(drivesData?.drives || []);
        setPlacementStats(statsData);
        setFilterOptions(filtersData || { companies: [], batches: [], statuses: [], drive_types: [] });
        setDrivesStatus('success');
      } catch (error) {
        if (!isActive || error.name === 'AbortError') return;
        console.warn('Failed to fetch placement data:', error);
        setDrivesError(error.message || 'Unable to load placement drives.');
        setDrivesStatus('error');
      }
    };

    loadPlacementData();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  // Filter and search drives
  const filteredDrives = useMemo(() => {
    return drives.filter(drive => {
      // Batch filter
      if (selectedBatch !== 'all' && drive.batch !== selectedBatch) return false;
      // Drive type filter
      if (selectedDriveType !== 'all' && drive.drive_type !== selectedDriveType) return false;
      // Location filter
      if (selectedLocation !== 'all' && drive.job_location !== selectedLocation) return false;
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesCompany = drive.company_name?.toLowerCase().includes(query);
        const matchesRole = drive.role?.toLowerCase().includes(query);
        const matchesBranches = drive.eligible_branches?.toLowerCase().includes(query);
        if (!matchesCompany && !matchesRole && !matchesBranches) return false;
      }
      return true;
    }).sort((a, b) => getDate(b) - getDate(a));
  }, [drives, selectedBatch, selectedDriveType, selectedLocation, searchQuery]);

  // Compute unique locations from drives
  const uniqueLocations = useMemo(() => {
    const locations = new Set(drives.map(d => d.job_location).filter(Boolean));
    return Array.from(locations).sort();
  }, [drives]);

  // Parse CTC helper
  const formatCTC = (ctcStr) => {
    if (!ctcStr) return null;
    // Already formatted nicely
    if (ctcStr.includes('LPA') || ctcStr.includes('lpa') || ctcStr.includes('Lakhs')) return ctcStr;
    if (ctcStr.includes('month') || ctcStr.includes('Month')) return ctcStr;
    // Try to parse number and format
    const match = ctcStr.match(/(\d+(?:\.\d+)?)/);
    if (match) {
      const num = parseFloat(match[1]);
      if (num > 1000) return `₹${(num / 100000).toFixed(1)} LPA`; // Large numbers might be annual
      return `₹${num} LPA`;
    }
    return ctcStr;
  };

  // Format deadline helper
  const formatDeadline = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    const formatted = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    if (diffDays < 0) return { text: formatted, urgent: false, passed: true };
    if (diffDays <= 3) return { text: `${formatted} (${diffDays}d left)`, urgent: true, passed: false };
    if (diffDays <= 7) return { text: `${formatted} (${diffDays}d left)`, urgent: false, passed: false };
    return { text: formatted, urgent: false, passed: false };
  };

  useEffect(() => {
    if (assistantMessagesRef.current) {
      assistantMessagesRef.current.scrollTop = assistantMessagesRef.current.scrollHeight;
    }
  }, [assistantMessages]);

  useEffect(() => {
    if (user?.email) {
      setResumeForm((prev) => ({ ...prev, email: user.email }));
    }
  }, [user?.email]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const loadResumes = async () => {
      setResumeStatus('loading');
      setResumeError(null);
      try {
        const payload = await listResumes({ signal: controller.signal });
        if (!isActive) {
          return;
        }
        setResumes(Array.isArray(payload?.resumes) ? payload.resumes : []);
        setResumeStatus('success');
      } catch (error) {
        if (!isActive || error.name === 'AbortError') {
          return;
        }
        setResumeError(error.message || 'Unable to load shared resumes.');
        setResumeStatus('error');
      }
    };

    loadResumes();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  const clampNumber = (value, min, max, fallback) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.min(max, Math.max(min, parsed));
    }
    return fallback;
  };

  const toFriendlyError = (error, fallback) => {
    if (!error) return fallback;
    const fromPayload = error?.payload?.message || error?.payload?.detail || error?.payload?.error;
    if (fromPayload) {
      return fromPayload;
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
    if (error.status) {
      return `Request failed (status ${error.status}).`;
    }
    return fallback;
  };

  const refreshResumes = async () => {
    setResumeStatus('loading');
    setResumeError(null);
    try {
      const payload = await listResumes();
      setResumes(Array.isArray(payload?.resumes) ? payload.resumes : []);
      setResumeStatus('success');
    } catch (error) {
      setResumeError(error.message || 'Unable to load shared resumes.');
      setResumeStatus('error');
    }
  };

  const handleResumeSubmit = async (event) => {
    event.preventDefault();
    const email = (resolvedEmail || '').trim();
    const name = (resumeForm.name || user?.name || user?.fullName || '').trim();

    if (!email) {
      setResumeError('Email is required to add a resume.');
      return;
    }

    if (!resumeFile) {
      setResumeError('Please upload a PDF resume.');
      return;
    }

    if (resumeFile && resumeFile.type !== 'application/pdf') {
      setResumeFileError('Please upload a PDF file.');
      return;
    }

    setResumeFileError(null);

    setResumeStatus('submitting');
    setResumeError(null);
    try {
      await addResumeFromPdf({
        file: resumeFile,
        name: name || null,
        email
      });

      setResumeForm({ ...DEFAULT_RESUME_FORM, email: user?.email || '' });
      setResumeFile(null);
      await refreshResumes();
    } catch (error) {
      setResumeError(error.message || 'Could not add the resume right now.');
      setResumeStatus('error');
    }
  };

  const handleAssistantSubmit = async (event) => {
    event.preventDefault();
    const trimmedQuestion = assistantQuestion.trim();

    if (!user) {
      setAssistantError('Sign in to use the resume bot.');
      return;
    }

    if (!trimmedQuestion) {
      setAssistantError('Ask a question so I can search the resume pool.');
      return;
    }

    const topK = clampNumber(assistantTopK, 1, 10, DEFAULT_ASSISTANT_TOPK);
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmedQuestion
    };

    setAssistantMessages((prev) => [...prev, userMessage]);
    setAssistantQuestion('');
    setAssistantStatus('loading');
    setAssistantError(null);

    try {
      const payload = await askPlacementQuestion({
        question: trimmedQuestion,
        topK
      });

      setAssistantMessages((prev) => ([
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: payload?.answer || 'I could not find an answer right now.',
          sources: Array.isArray(payload?.sources) ? payload.sources : []
        }
      ]));
      setAssistantStatus('success');
    } catch (error) {
      setAssistantError(toFriendlyError(error, 'Could not fetch an answer from the resume pool.'));
      setAssistantStatus('error');
    }
  };

  const handleResumeDelete = async (resumeId) => {
    if (!resumeId) {
      return;
    }
    setResumeStatus('deleting');
    setResumeError(null);
    try {
      await deleteResume({ resumeId });
      await refreshResumes();
    } catch (error) {
      setResumeError(error.message || 'Could not remove the resume right now.');
      setResumeStatus('error');
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    setLeaderboardStatus('loading');
    setLeaderboardError(null);

    fetchDsaLeaderboard({ signal: controller.signal })
      .then((response) => {
        if (!isActive) {
          return;
        }
        const normaliseProvider = (payload) => {
          const entries = Array.isArray(payload?.entries) ? payload.entries : [];
          return {
            entries,
            totalProfiles: toSafeCount(payload?.totalProfiles, entries.length)
          };
        };

        const nextData = {
          codeforces: normaliseProvider(response?.leaderboard?.codeforces),
          leetcode: normaliseProvider(response?.leaderboard?.leetcode)
        };

        setLeaderboardData(nextData);
        setLeaderboardSummary({
          generatedAt: response?.generatedAt ?? null,
          summary: response?.summary ?? null
        });
        setLeaderboardStatus('success');
      })
      .catch((error) => {
        if (!isActive || error.name === 'AbortError') {
          return;
        }
        setLeaderboardError(error.message || 'Unable to load the leaderboard right now.');
        setLeaderboardStatus('error');
      });

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  const leaderboardSections = useMemo(
    () => Object.entries(leaderboardData).map(([provider, payload]) => {
      const meta = LEADERBOARD_PROVIDER_META[provider] || {
        title: `${provider} leaderboard`,
        description: ''
      };
      const entries = Array.isArray(payload?.entries) ? payload.entries : [];
      return {
        provider,
        ...meta,
        entries,
        totalProfiles: toSafeCount(payload?.totalProfiles, entries.length)
      };
    }),
    [leaderboardData]
  );

  const leaderboardRefreshLabel = useMemo(() => {
    if (!leaderboardSummary?.generatedAt) {
      return null;
    }
    const timestamp = new Date(leaderboardSummary.generatedAt);
    if (Number.isNaN(timestamp.getTime())) {
      return null;
    }
    return timestamp.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }, [leaderboardSummary?.generatedAt]);

  const linkedProfilesLabel = useMemo(() => {
    const summary = leaderboardSummary?.summary;
    if (!summary) {
      return null;
    }

    const parts = [];
    const codeforcesProfiles = toSafeCount(summary?.codeforces?.totalProfiles);
    const leetcodeProfiles = toSafeCount(summary?.leetcode?.totalProfiles);

    if (codeforcesProfiles > 0) {
      parts.push(`Codeforces ${codeforcesProfiles}`);
    }
    if (leetcodeProfiles > 0) {
      parts.push(`LeetCode ${leetcodeProfiles}`);
    }

    if (parts.length === 0) {
      return null;
    }

    return `Profiles linked · ${parts.join(' • ')}`;
  }, [leaderboardSummary?.summary]);

  const isResumeBusy = useMemo(
    () => ['loading', 'submitting', 'deleting'].includes(resumeStatus),
    [resumeStatus]
  );

  return (
    <div className="placement-page">
      <section className="placement-hero animate-slide-up" aria-labelledby="placement-hero-title">
        <div className="placement-hero__intro">
          <p className="placement-hero__eyebrow">placements · readiness stack</p>
          <h1 id="placement-hero-title">Everything you need for campus placements, curated in one drive</h1>
          <p>
            Explore templates, problem sets, mock interview scripts, and roadmaps contributed by IIIT Network mentors and alumni.
          </p>
          <div className="placement-hero__actions">
            {/* <Button
              as="a"
              href={driveFolder}
              target="_blank"
              rel="noopener noreferrer"
              variant="gradient"
              className="primary hover-pulse"
              icon="external-link"
            >
              Open resource drive
            </Button> */}
            <Button
              as="a"
              href={`${driveFolder}#placement-checklist`}
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
            >
              Download 8-week checklist
            </Button>
          </div>
        </div>

        <div className="placement-hero__visual delay-200 animate-slide-up">
          <img src={placementHero3d} alt="Abstract 3D glass shapes representing future pathways" className="hero-3d-asset" />
        </div>
      </section>

      {/* Placement Dashboard Section */}
      <section className="placement-dashboard delay-500 animate-slide-up" aria-labelledby="dashboard-heading">
        <header className="dashboard-header">
          <div>
            <p className="dashboard-eyebrow">Placement Insights</p>
            <h2 id="dashboard-heading">Campus Placement Dashboard</h2>
            <p>Real-time placement data, company visits, and simplified tracking.</p>
          </div>
          {drivesStatus === 'success' && (
            <span className="live-badge">🟢 Live Pipeline</span>
          )}
        </header>

        {/* Dashboard Content */}
        {drivesStatus === 'loading' && (
          <div className="pipeline-stats-loading">
            <span className="loading-spinner"></span>
            <span>Loading placement drives...</span>
          </div>
        )}

        {drivesError && (
          <div className="pipeline-stats-error">
            <span className="error-icon">⚠️</span>
            <span>{drivesError}</span>
          </div>
        )}

        {drivesStatus === 'success' && (
          <>
            {/* Stats Overview */}
            <div className="dashboard-metrics pipeline-metrics">
              <Card className="metric-card metric-card--pipeline" variant="glass">
                <span className="metric-icon">🚀</span>
                <div className="metric-content">
                  <span className="metric-value">{drives.length}</span>
                  <span className="metric-label">Total Drives</span>
                  <span className="metric-source">Batch 2026</span>
                </div>
              </Card>
              {/* <Card className="metric-card metric-card--pipeline" variant="glass">
                <span className="metric-icon">📅</span>
                <div className="metric-content">
                  <span className="metric-value">
                    {drives.filter(d => d.status === 'upcoming' || d.status === 'open').length}
                  </span>
                  <span className="metric-label">Active & Upcoming</span>
                  <span className="metric-source">Apply Now</span>
                </div>
              </Card> */}
              <Card className="metric-card metric-card--pipeline" variant="glass">
                <span className="metric-icon">🏢</span>
                <div className="metric-content">
                  <span className="metric-value">{placementStats?.total_companies || 0}</span>
                  <span className="metric-label">Unique Companies</span>
                  <span className="metric-source">Visiting Campus</span>
                </div>
              </Card>
            </div>

            {/* Placement Timeline (Scatter Plot) */}
            {drives.length > 0 && (() => {


              const sortedDrives = [...drives].sort((a, b) => getDate(a) - getDate(b));

              const drivesWithCtc = sortedDrives.map(d => ({ ...d, ctc: parseCTC(d.ctc_or_stipend) }));
              // Filter: Show only under 50 LPA AND after May 2024 as requested
              const filteredGraphDrives = drivesWithCtc.filter(d => {
                const date = getDate(d);
                // Assume 'after May' refers to May 2024 or just general cutoff.
                // Given "Batch 2025 & 2026", May 2024 is the start of placement season for 2025.
                const mayFirst = new Date('2024-05-01');
                return d.ctc <= 50 && date >= mayFirst;
              });

              const dates = filteredGraphDrives.map(d => getDate(d));
              const minDate = new Date(Math.min(...dates));
              const maxDate = new Date(Math.max(...dates));
              const dateRange = maxDate - minDate || 1;

              const ctcs = filteredGraphDrives.map(d => d.ctc);
              const maxCTC = Math.max(...ctcs, 20);
              const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

              return (
                <div className="dashboard-section scatter-plot-section">
                  <div className="section-header">
                    <h3>📊 Placement Timeline</h3>
                    <div className="section-header-actions">
                      <span className="badge badge--success">Under 50 LPA</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowLabels(!showLabels)}
                        className="chart-toggle-btn"
                      >
                        {showLabels ? 'Hide Labels' : 'Show Labels'}
                      </Button>
                    </div>
                  </div>
                  <Card className="scatter-chart-container" variant="default">
                    <div className="scatter-chart">
                      {/* Y-axis */}
                      <div className="scatter-y-axis">
                        <span>₹{Math.ceil(maxCTC)} LPA</span>
                        <span>₹{Math.ceil(maxCTC / 2)} LPA</span>
                        <span>₹0</span>
                      </div>
                      {/* Plot Area */}
                      <div className="scatter-plot-area">
                        <div className="scatter-grid">
                          <div className="grid-line" style={{ bottom: '0%' }}></div>
                          <div className="grid-line" style={{ bottom: '50%' }}></div>
                          <div className="grid-line" style={{ bottom: '100%' }}></div>
                        </div>
                        <div className="scatter-points">
                          {filteredGraphDrives.map((drive, index) => {
                            const date = getDate(drive);
                            const xPos = ((date - minDate) / dateRange) * 100;
                            const yPos = (drive.ctc / maxCTC) * 100;

                            return (
                              <div
                                key={drive.id || index}
                                className="scatter-point"
                                style={{
                                  left: `${Math.max(2, Math.min(98, xPos))}%`,
                                  bottom: `${Math.max(5, Math.min(95, yPos))}%`,
                                }}
                                title={`${drive.company_name} - ₹${drive.ctc.toFixed(1)} LPA`}
                              >
                                <div className={`scatter-tooltip ${showLabels ? 'visible' : ''}`}>
                                  <strong>{drive.company_name}</strong>
                                  <span className="scatter-date">{formatDate(date)}</span>
                                  <span className="scatter-role">₹{drive.ctc.toFixed(1)} LPA</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })()}

            {/* Filters & Search */}
            <div className="dashboard-controls">
              <div className="search-bar-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search companies, roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="dashboard-search-input"
                />
              </div>

              <div className="dashboard-filters">
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className="dashboard-select"
                >
                  <option value="all">All Batches</option>
                  {filterOptions.batches.map(b => (
                    <option key={b} value={b}>Batch {b}</option>
                  ))}
                </select>

                <select
                  value={selectedDriveType}
                  onChange={(e) => setSelectedDriveType(e.target.value)}
                  className="dashboard-select"
                >
                  <option value="all">All Types</option>
                  <option value="internship">Internships</option>
                  <option value="fte">Full Time</option>
                  <option value="both">Both</option>
                </select>

                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="dashboard-select"
                >
                  <option value="all">All Locations</option>
                  {uniqueLocations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Drives Grid */}
            <div className="drive-cards-grid">
              {filteredDrives.length === 0 ? (
                <div className="no-drives-found">
                  <span className="no-drives-icon">🔭</span>
                  <p>No drives match your filters.</p>
                  <Button variant="ghost" onClick={() => {
                    setSelectedBatch('all');
                    setSelectedDriveType('all');
                    setSelectedLocation('all');
                    setSearchQuery('');
                  }}>Clear Filters</Button>
                </div>
              ) : (
                filteredDrives.map((drive) => {
                  const deadline = formatDeadline(drive.registration_deadline);
                  const isClosed = drive.status === 'closed' || (drive.registration_deadline && new Date(drive.registration_deadline) < new Date());
                  const ctcDisplay = formatCTC(drive.ctc_or_stipend);

                  return (
                    <Card key={drive.id} className={`drive-card ${isClosed ? 'drive-card--closed' : ''}`} variant="default">
                      <div className="drive-card__header">
                        <div className="drive-company-info">
                          <div className="drive-company-logo-placeholder">
                            {drive.company_name.charAt(0)}
                          </div>
                          <div>
                            <h3 className="drive-company-name">{drive.company_name}</h3>
                            <div className="drive-badges">
                              {drive.drive_type && (
                                <span className={`drive-badge drive-badge--type`}>
                                  {drive.drive_type === 'both' ? 'FTE + Intern' : drive.drive_type}
                                </span>
                              )}
                              {drive.batch && <span className="drive-badge drive-badge--batch">{drive.batch}</span>}
                            </div>
                          </div>
                        </div>
                        {drive.confidence_score < 0.8 && (
                          <span className="confidence-warning" title="AI Verification Low">⚠️ Verify</span>
                        )}
                      </div>

                      <div className="drive-card__body">
                        {drive.role && (
                          <div className="drive-info-row">
                            <span className="drive-info-icon">💼</span>
                            <span className="drive-role">{drive.role}</span>
                          </div>
                        )}

                        {(ctcDisplay || drive.job_location) && (
                          <div className="drive-secondary-info">
                            {ctcDisplay && (
                              <span className="drive-ctc">
                                💰 {ctcDisplay}
                              </span>
                            )}
                            {drive.job_location && (
                              <span className="drive-location">
                                📍 {drive.job_location}
                              </span>
                            )}
                          </div>
                        )}

                        {drive.eligible_branches && (
                          <div className="drive-eligibility">
                            <span className="eligibility-label">Eligibility:</span>
                            <span className="eligibility-value">{drive.eligible_branches}</span>
                          </div>
                        )}

                        <div className="drive-deadline-section">
                          {deadline ? (
                            <p className={`drive-deadline ${deadline.urgent ? 'deadline-urgent' : ''} ${deadline.passed ? 'deadline-passed' : ''}`}>
                              {deadline.passed ? 'Closed on ' : 'Deadline: '}
                              <strong>{deadline.text}</strong>
                            </p>
                          ) : (
                            <p className="drive-deadline">No deadline specified</p>
                          )}
                        </div>
                      </div>

                      <div className="drive-card__footer">
                        <span className="drive-email-date">
                          Last Email Received: {getDate(drive).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </>
        )}
      </section>

      <section className="mcp-assistant delay-500 animate-slide-up" aria-labelledby="mcp-assistant-heading">
        <header className="mcp-assistant__header">
          <div>
            <p className="mcp-assistant__eyebrow">IIIT Network resume pool</p>
            <h2 id="mcp-assistant-heading">Share your resume with the community</h2>
            <p>Publish your resume so peers and mentors can discover profiles easily. Upload a PDF or add details, then ask the bot questions grounded in this pool.</p>
          </div>
        </header>

        <Card className="mcp-card mcp-card--wide resume-assistant" variant="default">
          <header className="mcp-card__header">
            <div>
              <p className="mcp-card__eyebrow">Resume assistant</p>
              <h3>Add resumes and ask the bot</h3>
              <p>Upload your PDF or add details so others can browse profiles and get grounded answers.</p>
            </div>
            <div className="resume-assistant__badges">
              <span className="status-pill status-pill--muted">{`${resumes.length} resumes indexed`}</span>
              <Button
                variant="ghost"
                onClick={refreshResumes}
                disabled={isResumeBusy}
                loading={resumeStatus === 'loading'}
                size="sm"
              >
                Refresh pool
              </Button>
            </div>
            {user && (
              <div className="resume-assistant__panel resume-assistant__panel--chat">
                <div className="resume-chat__header">
                  <div>
                    <p className="mcp-card__eyebrow">Ask the resume bot</p>
                    <h4>Grounded answers from the pool</h4>
                    <p className="mcp-assistant__hint">The bot will cite which resumes were used to answer.</p>
                  </div>
                  <div className="resume-chat__meta">
                    <span className="status-pill status-pill--muted">Top {assistantTopK || DEFAULT_ASSISTANT_TOPK} sources</span>
                  </div>
                </div>

                <div className="resume-chat__samples" aria-label="Suggested prompts">
                  {RESUME_ASSISTANT_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      className="ghost-sample"
                      onClick={() => setAssistantQuestion(prompt)}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>

                <div className="resume-chat__shell">
                  <div className="resume-chat__messages" ref={assistantMessagesRef} aria-live="polite">
                    {assistantMessages.map((message) => (
                      <article
                        key={message.id}
                        className={`resume-chat__message resume-chat__message--${message.role}`}
                      >
                        <div className="resume-chat__role">{message.role === 'assistant' ? 'Assistant' : 'You'}</div>
                        <p className="resume-chat__body">{message.content}</p>
                        {Array.isArray(message.sources) && message.sources.length > 0 && (
                          <div className="resume-chat__sources">
                            {message.sources.map((source) => (
                              <span key={source.id} className="resume-chat__source-chip">
                                {source.name}
                                {source.role && <span className="resume-chat__source-role"> · {source.role}</span>}
                                {Array.isArray(source.skills) && source.skills.length > 0 && (
                                  <span className="resume-chat__source-skills"> — {source.skills.join(', ')}</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    ))}
                    {assistantStatus === 'loading' && (
                      <article className="resume-chat__message resume-chat__message--assistant resume-chat__message--pending">
                        <div className="resume-chat__role">Assistant</div>
                        <p className="resume-chat__body">Thinking with the resume pool…</p>
                      </article>
                    )}
                  </div>

                  {assistantError && <p className="mcp-error">{assistantError}</p>}

                  <form className="resume-chat__composer" onSubmit={handleAssistantSubmit}>
                    <label className="sr-only" htmlFor="assistant-question">Ask a question</label>
                    <textarea
                      id="assistant-question"
                      rows="3"
                      value={assistantQuestion}
                      onChange={(event) => setAssistantQuestion(event.target.value)}
                      placeholder="Ask anything about the resumes in the pool…"
                    />
                    <div className="resume-chat__controls">
                      <div className="mcp-inline-fields">
                        <label htmlFor="assistant-topk">Sources to cite</label>
                        <input
                          id="assistant-topk"
                          type="number"
                          min="1"
                          max="10"
                          value={assistantTopK}
                          onChange={(event) => setAssistantTopK(event.target.value)}
                        />
                      </div>
                      <Button variant="primary" type="submit" loading={assistantStatus === 'loading'} disabled={assistantStatus === 'loading'}>
                        Ask the bot
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </header>

          <div className="resume-assistant__grid">
            <div className="resume-assistant__panel resume-assistant__panel--pool">
              <div className="resume-pool__summary">
                <p>Upload a PDF to make it searchable. Only your email is required; we will not show the full resume text.</p>
                {resumeStatus === 'error' && resumeError && <p className="mcp-error">{resumeError}</p>}
                {resumeStatus === 'loading' && <p className="mcp-inline-status">Loading current pool…</p>}
              </div>

              <form className="mcp-form mcp-form--grid resume-pool__form" onSubmit={handleResumeSubmit}>
                <div className="form-group">
                  <label htmlFor="resume-email">Email</label>
                  <input
                    id="resume-email"
                    type="email"
                    value={resolvedEmail}
                    onChange={user ? undefined : ((event) => setResumeForm({ ...resumeForm, email: event.target.value }))}
                    readOnly={!!user}
                    placeholder={user ? 'Using your account email' : 'name@example.com'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="resume-file">Upload PDF</label>
                  <input
                    id="resume-file"
                    type="file"
                    accept="application/pdf"
                    onChange={(event) => setResumeFile(event.target.files?.[0] || null)}
                  />
                </div>
                <div className="mcp-actions">
                  <Button variant="primary" type="submit" disabled={isResumeBusy} loading={resumeStatus === 'submitting'}>
                    Upload to pool
                  </Button>
                  {resumeError && <span className="mcp-error">{resumeError}</span>}
                  {resumeFileError && <span className="mcp-error">{resumeFileError}</span>}
                </div>
              </form>

              <div className="mcp-resume-list" aria-live="polite">
                {resumeStatus !== 'loading' && resumes.length === 0 && (
                  <p className="mcp-inline-status">No resumes added yet. Be the first to publish.</p>
                )}
                {resumes.length > 0 && (
                  <div className="mcp-resume-grid">
                    {resumes.map((resume) => (
                      <article key={resume.id} className="mcp-resume-chip">
                        <div className="mcp-resume-chip__top">
                          <div>
                            <h5>{resume.name || 'Resume added'}</h5>
                            <p>{resume.email}</p>
                          </div>
                        </div>
                        <p className="mcp-resume-chip__summary">Email captured. Resume stored securely.</p>
                        <div className="mcp-resume-chip__actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger"
                            onClick={() => handleResumeDelete(resume.id)}
                            disabled={isResumeBusy}
                          >
                            Remove
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </section>

      <section className="resource-collections delay-500 animate-slide-up" aria-labelledby="resource-collections-heading">
        <header>
          <h2 id="resource-collections-heading">Resource collections</h2>
          <p>Jump into the collections that map directly to the Drive sub-folders. Each card highlights what you will find and suggests how to use it.</p>
        </header>
        {resourceCollections.map((collection) => (
          <Card key={collection.id} className="resource-collection hover-pulse" aria-labelledby={`collection-${collection.id}`}>
            <div className="resource-collection__intro">
              <span className="resource-collection__icon" aria-hidden>{collection.icon}</span>
              <div>
                <h3 id={`collection-${collection.id}`}>{collection.title}</h3>
                <p>{collection.description}</p>
              </div>
              <Button
                as="a"
                variant="ghost"
                href={collection.resources[0]?.link || driveFolder}
                target="_blank"
                rel="noopener noreferrer"
              >
                {collection.ctaLabel}
              </Button>
            </div>
            {collection.id === 'dsa' && (
              <div className="dsa-leaderboard" role="region" aria-live="polite">
                <div className="dsa-leaderboard__header">
                  <h4>IIIT Network CP leaderboards</h4>
                  <p>Live ratings pulled directly from Codeforces and LeetCode profiles shared by the community.</p>
                  {leaderboardRefreshLabel && (
                    <span className="dsa-leaderboard__timestamp">Updated {leaderboardRefreshLabel}</span>
                  )}
                  {linkedProfilesLabel && (
                    <span className="dsa-leaderboard__meta">{linkedProfilesLabel}</span>
                  )}
                </div>

                {leaderboardStatus === 'loading' && (
                  <p className="dsa-leaderboard__status">Loading leaderboard…</p>
                )}

                {leaderboardStatus === 'error' && (
                  <p className="dsa-leaderboard__status dsa-leaderboard__status--error">
                    {leaderboardError || 'Unable to load the leaderboard right now.'}
                  </p>
                )}

                {leaderboardStatus === 'success' && (
                  <div className="dsa-leaderboard__lists">
                    {leaderboardSections.map((section) => {
                      const emptyMessage = section.provider === 'codeforces'
                        ? 'Link your Codeforces handle in your IIIT Network profile to appear on this board.'
                        : 'Link your LeetCode username in your IIIT Network profile to appear on this board.';

                      return (
                        <div
                          key={section.provider}
                          className="leaderboard-section"
                          aria-labelledby={`leaderboard-${section.provider}`}
                        >
                          <div className="leaderboard-section__header">
                            <h5 id={`leaderboard-${section.provider}`}>{section.title}</h5>
                            {section.description && <p>{section.description}</p>}
                            {section.totalProfiles > 0 && (
                              <span className="leaderboard-section__meta">
                                {`Profiles linked: ${section.totalProfiles}`}
                              </span>
                            )}
                          </div>

                          {section.entries.length === 0 ? (
                            <p className="leaderboard-section__empty">{emptyMessage}</p>
                          ) : (
                            <ol className="leaderboard-list">
                              {section.entries.map((entry) => {
                                const itemKey = entry.userId || `${section.provider}-${entry.rank}-${entry.handle || entry.name}`;
                                const subtitle = entry.subtitle
                                  || (entry.handle ? `@${entry.handle}` : null)
                                  || 'IIIT Network member';
                                const stats = Array.isArray(entry.stats) ? entry.stats : [];
                                const solvedStat = stats.find((stat) => stat?.label === 'Solved');
                                const rankStat = stats.find((stat) => stat?.label === 'Rank');

                                let primaryLabel = entry.ratingLabel || 'Rating';
                                let primaryValueText = '—';

                                if (Number.isFinite(entry.rating)) {
                                  primaryValueText = entry.rating.toLocaleString();
                                } else if (solvedStat && solvedStat.value != null) {
                                  const solvedValue = Number.isFinite(solvedStat.value)
                                    ? solvedStat.value
                                    : Number(solvedStat.value);
                                  primaryLabel = 'Questions solved';
                                  primaryValueText = Number.isFinite(solvedValue)
                                    ? solvedValue.toLocaleString()
                                    : String(solvedStat.value);
                                } else if (rankStat && rankStat.value != null) {
                                  const rankValue = Number.isFinite(rankStat.value)
                                    ? rankStat.value
                                    : Number(rankStat.value);
                                  primaryLabel = 'Global rank';
                                  primaryValueText = Number.isFinite(rankValue)
                                    ? `#${rankValue.toLocaleString()}`
                                    : `#${rankStat.value}`;
                                }

                                const secondaryStats = stats.filter((stat) => {
                                  if (!stat) {
                                    return false;
                                  }
                                  if (primaryLabel === 'Questions solved' && stat.label === 'Solved') {
                                    return false;
                                  }
                                  if (primaryLabel === 'Global rank' && stat.label === 'Rank') {
                                    return false;
                                  }
                                  return true;
                                });

                                return (
                                  <li key={itemKey} className="leaderboard-item">
                                    <div className="leaderboard-item__top">
                                      <span className="leaderboard-item__rank" aria-hidden>#{entry.rank}</span>
                                      <div className="leaderboard-item__identity">
                                        <h5>{entry.name}</h5>
                                        <p>{subtitle}</p>
                                      </div>
                                      <div className="leaderboard-item__score">
                                        <strong>{primaryValueText}</strong>
                                        <span>{primaryLabel}</span>
                                        {secondaryStats.map((stat) => {
                                          if (!stat) {
                                            return null;
                                          }
                                          const rawValue = stat.value ?? stat.count;
                                          if (rawValue === undefined || rawValue === null || rawValue === '') {
                                            return null;
                                          }
                                          return (
                                            <span key={stat.label} className="leaderboard-item__score-sub">
                                              {stat.label}: {rawValue}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </section>
    </div >
  );
};

export default PlacementResources;
